/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// React
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// React Router
import type { useFetcher } from 'react-router';

// Third-party
import debounce from 'lodash.debounce';

// Components
import { useToast } from '@/components/toast';

// Hooks
import { useConfig } from '@salesforce/storefront-next-runtime/config';

// Constants
import { MAX_LINE_ITEM_QUANTITY } from '@/lib/cart-constants';
import { resourceRoutes } from '@/route-paths';
// Providers
import { useBasketUpdater } from '@/providers/basket';
// Types
import type { ShopperBasketsV2 } from '@/scapi';
import type { ActionResponse } from '@/routes/types/action-responses';
import { useTranslation } from 'react-i18next';

/**
 * The constraint `{ success?: boolean }` is intentionally weak: this hook submits to two
 * different action routes (`removeAction` from config, `/action/cart-item-update`) whose
 * full response shapes differ, but the hook only reads `fetcher.data?.success`. Callers
 * pin a richer type if they want narrower access.
 */
interface UseCartQuantityUpdateProps<TResponse extends { success?: boolean }> {
    /** Cart item ID for API calls */
    itemId: string;
    /** Initial quantity value */
    initialValue: number;
    /** Stock level for validation */
    stockLevel?: number;
    /** Debounce delay in milliseconds */
    debounceDelay?: number;
    /** Fetcher used to submit the quantity update / remove. */
    fetcher: ReturnType<typeof useFetcher<TResponse>>;
}

interface UseCartQuantityUpdateReturn {
    /** Current quantity value (can be string for empty input) */
    quantity: number | string;
    /** Stock validation error message */
    stockValidationError: string | null;
    /** Maximum quantity allowed based on stock level (undefined if no limit) */
    stockMax: number | undefined;
    /** Whether to show remove confirmation dialog */
    showRemoveConfirmation: boolean;
    /** Handle quantity change from input */
    handleQuantityChange: (stringValue: string, numberValue: number) => void;
    /** Handle quantity input blur */
    handleQuantityBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    /** Handle keeping item (cancel removal) */
    handleKeepItem: () => void;
    /** Handle removing item (confirm removal) */
    handleRemoveItem: () => void;
    /** Set remove confirmation dialog visibility */
    setShowRemoveConfirmation: (show: boolean) => void;
}

/**
 * Custom hook for managing cart quantity updates with debouncing, stock validation, and API integration.
 *
 * This hook provides:
 * - Debounced API calls to prevent spam
 * - Stock level validation with error messages
 * - Optimistic updates for better UX
 * - Error handling with rollback on failure
 * - Remove confirmation dialog management
 *
 * @param props - Hook configuration
 * @returns Object containing state and handlers for cart quantity management
 *
 * @example
 * ```tsx
 * const {
 *   quantity,
 *   stockValidationError,
 *   showRemoveConfirmation,
 *   handleQuantityChange,
 *   handleQuantityBlur,
 *   handleKeepItem,
 *   handleRemoveItem,
 *   setShowRemoveConfirmation
 * } = useCartQuantityUpdate({
 *   itemId: 'cart-item-123',
 *   initialValue: 2,
 *   stockLevel: 10,
 *   debounceDelay: 750
 * });
 * ```
 */
export function useCartQuantityUpdate<TResponse extends { success?: boolean } = ActionResponse>({
    itemId,
    initialValue,
    stockLevel,
    debounceDelay,
    fetcher,
}: UseCartQuantityUpdateProps<TResponse>): UseCartQuantityUpdateReturn {
    const config = useConfig();
    const { addToast } = useToast();
    const { t } = useTranslation('quantitySelector');
    const updateBasket = useBasketUpdater();

    const effectiveDebounceDelay = debounceDelay || config.pages.cart.quantityUpdateDebounce;
    const removeAction = config.pages.cart.removeAction;

    // Effective ceiling for this line item: the lesser of the per-line-item
    // maximum (15) and the available-to-sell stock. When stock is unknown the
    // 15 ceiling still applies. Used for the inventory message, the typed-value
    // guard, and the picker `max` so no out-of-bounds PATCH is fired.
    const effectiveMax =
        stockLevel !== undefined && stockLevel > 0
            ? Math.min(MAX_LINE_ITEM_QUANTITY, stockLevel)
            : MAX_LINE_ITEM_QUANTITY;
    const stockIsBindingLimit = stockLevel !== undefined && stockLevel > 0 && stockLevel < MAX_LINE_ITEM_QUANTITY;

    // Remove item function
    const removeItem = useCallback(() => {
        if (!itemId) return;

        const formData = new FormData();
        formData.append('itemId', itemId);
        void fetcher.submit(formData, {
            method: 'POST',
            action: removeAction,
        });
    }, [itemId, removeAction, fetcher]);

    const [stockValidationError, setStockValidationError] = useState<string | null>(() => {
        if (initialValue < effectiveMax) return null;
        return stockIsBindingLimit ? t('maxStockReached') : t('maxQuantityReached');
    });
    const [quantity, setQuantity] = useState<number | string>(initialValue);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

    // Track the last quantity that was successfully confirmed by the API
    const [lastSuccessfulQuantity, setLastSuccessfulQuantity] = useState<number>(initialValue);
    // Track which quantity is currently being processed by an API call
    const [pendingQuantity, setPendingQuantity] = useState<number | null>(null);

    // Use refs to access current values in callbacks without stale closures
    const lastSuccessfulQuantityRef = useRef<number>(initialValue);
    const quantityRef = useRef<number | string>(initialValue);
    // True while the shopper has a locally-initiated quantity change that the
    // server has not yet confirmed. Guards the basket-sync effect so a slower
    // earlier revalidation can't briefly overwrite a newer optimistic value
    // (the 15→14→15 flicker seen when stepping up to the cap quickly).
    const pendingUserChangeRef = useRef(false);

    // Generate inventory message when quantity reaches the effective ceiling.
    // Distinguish the stock-limited case (maxStockReached) from the 15-unit
    // per-line-item cap (maxQuantityReached).
    const getInventoryMessage = useCallback(
        (qty: number) => {
            if (qty < effectiveMax) return null;
            return stockIsBindingLimit ? t('maxStockReached') : t('maxQuantityReached');
        },
        [effectiveMax, stockIsBindingLimit, t]
    );

    // Create debounced function with current values
    const changeItemQuantity = useMemo(() => {
        return debounce((newQuantity: number) => {
            // Don't make API call if quantity exceeds the effective ceiling
            // (the lesser of stock and the 15-unit per-line-item maximum).
            if (newQuantity > effectiveMax) {
                return;
            }

            // Track the quantity that triggered this API call
            setPendingQuantity(newQuantity);

            const formData = new FormData();
            formData.append('itemId', itemId);
            formData.append('quantity', newQuantity.toString());

            void fetcher.submit(formData, {
                method: 'PATCH',
                action: resourceRoutes.cartItemUpdate,
            });
        }, effectiveDebounceDelay);
        // effectiveDebounceDelay: stable value, no need to recreate effect
        // fetcher: stable fetcher, no need to recreate effect
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, effectiveMax]);

    // Handle quantity change with cart-specific logic
    const handleQuantityChange = useCallback(
        (stringValue: string, numberValue: number) => {
            // Handle empty input - allow user to clear input without showing remove confirmation
            if (stringValue === '') {
                setQuantity(stringValue);
                setStockValidationError(null);
                return;
            }

            // Handle 0 input - show remove confirmation
            if (numberValue === 0) {
                setQuantity(0); // Show 0 in the input field
                setShowRemoveConfirmation(true);
                return;
            }

            // Handle increment from empty state - change to 1
            if (quantityRef.current === '' && numberValue === 1) {
                setQuantity(numberValue);
                setStockValidationError(null);

                // Cancel any pending handlers before making new API call
                changeItemQuantity.cancel();

                // Mark the local change as unconfirmed until the API responds
                pendingUserChangeRef.current = true;

                // Make API call
                changeItemQuantity(numberValue);
                return;
            }

            // Set the Quantity of product to value of input if value number
            if (numberValue >= 0) {
                // Always update the quantity display value for immediate UI feedback
                setQuantity(numberValue);

                // Show or clear stock validation message
                setStockValidationError(getInventoryMessage(numberValue));

                // Check if quantity exceeds the effective ceiling (min(15, stock))
                if (numberValue > effectiveMax) {
                    // Cancel any pending debounced API calls
                    changeItemQuantity.cancel();

                    // Don't make API call if quantity exceeds the ceiling
                    return;
                }

                // Cancel any pending handlers before making new API call
                changeItemQuantity.cancel();

                // Mark the local change as unconfirmed until the API responds
                pendingUserChangeRef.current = true;

                // Make API call
                changeItemQuantity(numberValue);
            }
        },
        [effectiveMax, getInventoryMessage, changeItemQuantity]
    );

    // Handle quantity blur - default to last successful update if user leaves with empty input
    const handleQuantityBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        const { value: inputValue } = e.target;

        // Handle empty input - reset to last successful update
        if (!inputValue || inputValue === '') {
            setQuantity(lastSuccessfulQuantityRef.current);
            setStockValidationError(null);
            return;
        }

        // Handle 0 input - show remove confirmation
        if (inputValue === '0') {
            setShowRemoveConfirmation(true);
        }
    }, []);

    // Handle remove confirmation - keep item
    const handleKeepItem = useCallback(() => {
        setShowRemoveConfirmation(false);
        // Reset quantity to last successful update to keep the item
        setQuantity(lastSuccessfulQuantityRef.current);
    }, []);

    // Handle remove confirmation - remove item
    const handleRemoveItem = useCallback(() => {
        setShowRemoveConfirmation(false);
        removeItem();
    }, [removeItem]);

    // Handle API response when API call completes
    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data) {
            if (fetcher.data.success) {
                // SSF-557 — Publish the mutated basket into BasketProvider so
                // downstream `useBasket()` consumers (checkout Payment section
                // → `cart?.paymentInstruments?.[0]`, header cart badge fallback,
                // etc.) observe the post-hook truth. Mirrors the pattern already
                // used by `kit-quantity-picker` and the Extend toggle. Without
                // this write, a qty change that triggers the
                // `PaymentInstrumentInvalidator` (Σ(PI.amount) drifted from the
                // new `totalGrossPrice`) removes the PI on SFCC but the client
                // never sees it: `BasketProvider.current` keeps the pre-edit
                // basket (with PI attached) and the `_checkout.checkout.tsx`
                // seed guard (`providerBasket.basketId === basket.basketId`)
                // skips re-seeding on the next visit, so the checkout Payment
                // card still renders the just-removed PI's summary.
                const responseBasket = (fetcher.data as { basket?: ShopperBasketsV2.schemas['Basket'] }).basket;
                if (responseBasket?.basketId) {
                    updateBasket(responseBasket);
                }
                // Update the last successful quantity to the pending quantity that triggered this API call
                if (pendingQuantity !== null) {
                    setLastSuccessfulQuantity(pendingQuantity);
                    lastSuccessfulQuantityRef.current = pendingQuantity;
                    setPendingQuantity(null);
                }
                // Server confirmed the local change; allow basket sync to resume
                pendingUserChangeRef.current = false;
                addToast(t('quantityUpdated'), 'success');
            } else {
                // On failure, reset to the last known good value
                setQuantity(lastSuccessfulQuantityRef.current);
                setPendingQuantity(null);
                pendingUserChangeRef.current = false;
                addToast(t('quantityUpdateFailed'), 'error');
            }
        }
        //As addToast is unlikely to change, we don't need to include it in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher.state, fetcher.data, itemId]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            changeItemQuantity.cancel();
        };
        // changeItemQuantity: stable debounced function, no need to recreate effect
        // Only depend on itemId to avoid premature cleanup
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    // Keep refs in sync with state
    useEffect(() => {
        quantityRef.current = quantity;
    }, [quantity]);

    useEffect(() => {
        lastSuccessfulQuantityRef.current = lastSuccessfulQuantity;
    }, [lastSuccessfulQuantity]);

    // Update local quantity and stock validation when initialValue or stockLevel changes
    // (e.g., from external basket updates or async data loading).
    //
    // Skip while the shopper has an unconfirmed local change in flight: a slower
    // earlier revalidation (e.g. when stepping 13→14→15 quickly) can otherwise
    // momentarily overwrite the newer optimistic value with the stale basket
    // quantity, which surfaced as a 15→14→15 flicker at the per-line-item cap.
    useEffect(() => {
        if (pendingUserChangeRef.current) return;

        setQuantity(initialValue);
        setLastSuccessfulQuantity(initialValue);
        setStockValidationError(getInventoryMessage(initialValue));
    }, [initialValue, stockLevel, getInventoryMessage]);

    // Surface the effective ceiling (min(15, stock)) as the picker's max so the
    // stepper's increment button and blur-clamp stop at the right value.
    const stockMax = effectiveMax;

    return {
        quantity,
        stockValidationError,
        stockMax,
        showRemoveConfirmation,
        handleQuantityChange,
        handleQuantityBlur,
        handleKeepItem,
        handleRemoveItem,
        setShowRemoveConfirmation,
    };
}
